import { useCallback } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, Text, Skeleton } from '@nula/core-ui';
import * as Sentry from '@sentry/react';
import { useConversationList } from '../hooks/use-conversation-list';
import { useAssistantChatContext } from './assistant-chat-provider';
import { useConversationRegistryStore } from '../stores/use-conversation-registry-store';
import { useEntityStore } from '@/lib/sync/entity-store';
import { sessionOpenTab } from '@/session';
import { updateConversation, deleteConversation } from '../api/conversations';
import { ConversationListItem } from './conversation-list-item';
import styles from './conversation-history-list.module.css';

interface ConversationHistoryListProps {
  className?: string;
  /**
   * Fired after a conversation is selected (and loadConversation kicked off).
   * Used by the right-side panel to close itself once the user picks a chat,
   * so they land back on the now-loaded conversation ("open over" model).
   */
  onAfterSelect?: () => void;
}

const root = cva(styles.root);

export function ConversationHistoryList({
  className,
  onAfterSelect,
}: ConversationHistoryListProps) {
  const conversations = useConversationList();
  const { startNewConversation } = useAssistantChatContext();
  const currentConversationId = useConversationRegistryStore((s) => s.currentConversationId);
  // cursor is null before the first sync bootstrap completes. Only show the
  // "No conversations yet" empty state once we know the server has been queried.
  const isBootstrapped = useEntityStore((s) => s.cursor !== null);

  const handleSelect = useCallback(
    (id: string) => {
      // Open the conversation as its OWN tab (tab id = conversation id, type
      // 'conversation') so it mounts a per-tab Chat instance via the
      // connection-scoped registry — NOT mutate the shared chat via
      // loadConversation, which pollutes the current window with the selected
      // conversation's state.
      sessionOpenTab(id, 'conversation');
      onAfterSelect?.();
    },
    [onAfterSelect],
  );

  // Empty deps: these mutation handlers reference only module-level imports
  // (updateConversation, Sentry) — nothing reactive, so they're stable.
  const handlePin = useCallback(
    (id: string, pinned: boolean) => {
      updateConversation(id, { isPinned: pinned }).catch((err) => {
        Sentry.captureException(err, { extra: { action: 'pin', id } });
      });
    },
    [],
  );

  const handleDelete = useCallback(
    (id: string) => {
      deleteConversation(id).catch((err) => {
        Sentry.captureException(err, { extra: { action: 'delete', id } });
      });
      // If deleting the active conversation, start fresh
      if (id === currentConversationId) {
        startNewConversation();
      }
    },
    [currentConversationId, startNewConversation],
  );

  return (
    <Flex
      vertical
      className={clsx(root(), className)}
    >
      <Flex vertical className={styles.list}>
        {conversations.length === 0 && !isBootstrapped ? (
          <Flex className={styles.emptyState} justify="center" data-testid="conversation-history-loading">
            <Skeleton active paragraph={{ rows: 3 }} title={false} />
          </Flex>
        ) : conversations.length === 0 ? (
          <Flex className={styles.emptyState} justify="center">
            <Text size="sm" type="secondary">No conversations yet</Text>
          </Flex>
        ) : (
          conversations.map((row) => (
            <ConversationListItem
              key={row.id}
              row={row}
              active={row.id === currentConversationId}
              onSelect={handleSelect}
              onPin={handlePin}
              onDelete={handleDelete}
            />
          ))
        )}
      </Flex>
    </Flex>
  );
}
